import { Box, Typography, Stack, debounce } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { ECElementEvent } from 'echarts';
import { useEffect, useRef, useState } from 'react';
import CustomDropdown from 'components/Dropdown';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { InfoTooltip } from './InfoTooltip';
import { ProgressChartWidgetProps } from './type';
import { defaultOptions, getProgressChartOptions } from './chartWidgetUtility';
import LoaderComponent from 'components/Loader';
import { useTheme } from '@mui/material/styles';

const GRID_LEFT_CONFIG = {
  cost: [
    { maxWidth: 420, value: '21%' },
    { maxWidth: 600, value: '18%' },
    { maxWidth: 820, value: '15%' },
    { maxWidth: 920, value: '12%' },
    { maxWidth: 1260, value: '10%' },
    { maxWidth: Infinity, value: '8%' }
  ],
  nonCost: [
    { maxWidth: 420, value: '18%' },
    { maxWidth: 600, value: '15%' },
    { maxWidth: 820, value: '12%' },
    { maxWidth: 920, value: '9%' },
    { maxWidth: 1260, value: '7%' },
    { maxWidth: Infinity, value: '5%' }
  ]
};
const DARK_THEME_COLORS = {
  text: '#A7ADCB',
  border: 'rgba(255,255,255,0.2)',
  background: 'transparent',
  scrollHandle: '#A7ADCB',
  scrollFiller: 'rgba(255,255,255,0.15)'
};
const calculateGridLeft = (width: number, isCostChart: boolean, title: string): string => {
  if (title === 'Total Cost Spends') {
    return '2%';
  }
  const config = isCostChart ? GRID_LEFT_CONFIG.cost : GRID_LEFT_CONFIG.nonCost;
  return config.find(item => width < item.maxWidth)?.value || '5%';
};
const getThemeColors = (isLight: boolean, colors:any) => isLight ? {
  text: colors.widget?.chart?.legendTextColor,
  border: colors.widget?.chart?.legendInactiveUpDownIconColor,
  background: colors.widget?.background?.chart,
  scrollHandle: colors.widget?.chart?.legendActiveUpDownIconColor,
  scrollFiller: colors.widget?.chart?.emptyCircleFill
} : DARK_THEME_COLORS;
const getBaseChartOption = (
  result: any,
  gridLeft: string,
  seriesData: any[],
  defaultOptions: any
) => {
  if (!seriesData.length) {
    return defaultOptions;
  }

  return {
    ...result,
    grid: {
      ...result.grid,
      left: gridLeft
    }
  };
};

const getLightThemeOption = (adjustedOption: any, colors:any) => {
  if (!adjustedOption) return adjustedOption;

  return {
    ...adjustedOption,
    backgroundColor: colors.widget?.background?.chart,
    graphic: Array.isArray(adjustedOption.graphic) ? adjustedOption.graphic.map((item:any) => ({
      ...item,
      style: item.style ? { ...item.style, fill: colors.widget?.text?.noChange } : item.style
    })) : adjustedOption.graphic,
    xAxis: getXAxisLightTheme(adjustedOption.xAxis, colors),
    yAxis: getYAxisLightTheme(adjustedOption.yAxis, colors),
    tooltip: getTooltipLightTheme(adjustedOption.tooltip, colors),
    legend: getLegendLightTheme(adjustedOption.legend, colors)
  };
};

const getXAxisLightTheme = (xAxis: any, colors:any) => {
  if (!xAxis) return undefined;

  return {
    ...xAxis,
    axisLabel: { ...xAxis.axisLabel, color: colors.widget?.chart?.legendTextColor },
    axisLine: xAxis.axisLine ? { ...xAxis.axisLine, lineStyle: { ...xAxis.axisLine.lineStyle, color: colors.widget?.chart?.legendTextColor } } : xAxis.axisLine,
    splitLine: getSplitLineLightTheme(xAxis.splitLine, colors)
  };
};

const getSplitLineLightTheme = (splitLine: any, colors:any) => {
  if (!splitLine) return undefined;

  return {
    ...splitLine,
    lineStyle: { ...splitLine.lineStyle, color: colors.widget?.chart?.emptyCircleFill }
  };
};

const getYAxisLightTheme = (yAxis: any, colors:any) => {
  if (!yAxis) return undefined;

  return {
    ...yAxis,
    axisLabel: { ...yAxis.axisLabel, color: colors.widget?.chart?.legendTextColor },
    axisLine: yAxis.axisLine ? { ...yAxis.axisLine, lineStyle: { ...yAxis.axisLine.lineStyle, color: colors.widget?.chart?.legendTextColor } } : yAxis.axisLine,
    splitLine: getSplitLineLightTheme(yAxis.splitLine, colors)
  };
};

const getTooltipLightTheme = (tooltip: any, colors:any) => {
  if (!tooltip) return undefined;

  return {
    ...tooltip,
    backgroundColor: colors.widget?.chart?.tooltipBg,
    textStyle: { ...tooltip.textStyle, color: colors.widget?.chart?.tooltipText },
    borderColor: colors.widget?.chart?.legendInactiveUpDownIconColor
  };
};

const getLegendLightTheme = (legend: any, colors:any) => {
  if (!legend) return undefined;

  return {
    ...legend,
    textStyle: { ...legend.textStyle, color: colors.widget?.chart?.legendTextColor }
  };
};

const getScrollOption = (
  adjustedOption: any,
  maxVisibleBars: number,
  dataLength: number,
  isLight: boolean,
  colors:any
) => {
  const percentSpan = Math.min(100, (maxVisibleBars / dataLength) * 100);
  const startPercent = 100 - percentSpan;
  const endPercent = 100;
  const themeColors = getThemeColors(isLight, colors);

  return {
    ...adjustedOption,
    grid: {
      ...adjustedOption.grid,
      right: isLight ? '16%' : '14%',
    },
    dataZoom: [
      getSliderDataZoom(startPercent, endPercent, themeColors),
      getInsideDataZoom(startPercent, endPercent)
    ]
  };
};

const getSliderDataZoom = (startPercent: number, endPercent: number, colors:any) => ({
  type: 'slider',
  yAxisIndex: 0,
  start: startPercent,
  end: endPercent,
  right: 8,
  width: 12,
  borderColor: 'transparent',
  fillerColor: colors.scrollFiller,
  handleStyle: { color: colors.scrollHandle },
  textStyle: { color: colors.text, fontSize: 10 },
  dataBackground: {
    lineStyle: { color: colors.border },
    areaStyle: { color: 'transparent' },
  },
  showDetail: false,
  zoomLock: true,
  brushSelect: false,
  moveOnMouseMove: true,
});

const getInsideDataZoom = (startPercent: number, endPercent: number) => ({
  type: 'inside',
  yAxisIndex: 0,
  start: startPercent,
  end: endPercent,
  zoomOnMouseWheel: true,
  moveOnMouseMove: true,
  moveOnMouseWheel: true,
});

const shouldShowScroll = (
  useScroll: boolean,
  dataLength: number,
  chartByCost: number | undefined
): boolean => {
  return useScroll && dataLength > 0 && (chartByCost === 0 || chartByCost === 1);
};

const WidgetTitle = ({
  title,
  isOverlayWidget,
  isLight,
  tooltipLabel,
  colors
}: any) => (
  <Stack direction="row" gap={1} alignItems="start">
    <Typography
      variant={isOverlayWidget ? 'overlayWidgetTitle' : 'widgetTitle'}
      sx={isLight ? { color: `${colors.widget?.text?.title} !important` } : undefined}
    >
      {title}
    </Typography>
    {!!tooltipLabel && <WidgetTooltip tooltipLabel={tooltipLabel} />}
  </Stack>
);

const WidgetTooltip = ({ tooltipLabel }: any) => (


  <Box sx={{ position: "relative", zIndex: 10 }}>
    <InfoTooltip label={tooltipLabel} />
  </Box>
);
const getDropdownSx = (isLight: boolean, colors:any) => {
  if (isLight) {
    return {
      minWidth: 140,
      fontWeight: '700',
      background: 'transparent',
      '& .MuiSelect-select': {
        color: colors.DropdownV2?.menuItemText,
        fontSize: '14px',
        fontWeight: '700',
        py: 0.75,
        px: 1.5,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.DropdownV2?.borderColor,
      },
      '& .MuiSelect-icon': {
        color: colors.DropdownV2?.dropdownIconDefault,
      },
    };
  }

  return {
    minWidth: 140,
    backgroundColor: 'transparent',
    '& .MuiSelect-select': {
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '700',
      py: 0.75,
      px: 1.5,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.3)',
    },
    '& .MuiSelect-icon': {
      color: '#ffffff',
    },
  };
};

const getMenuSx = (isLight: boolean, colors:any) => ({
  PaperProps: {
    sx: {
      minWidth: 140,
      transition: 'opacity 234ms cubic-bezier(0.4, 0, 0.2, 1), transform 156ms cubic-bezier(0.4, 0, 0.2, 1)',
      backgroundColor: isLight ? colors.DropdownV2?.menuItemBackground : '#333333',
      marginTop: '4px',
      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
      borderRadius: '4px',
      position: 'absolute',
      marginLeft: '-51px !important'
    },
  },
  MenuListProps: {
    sx: {
      minWidth: 180,
      paddingTop: 0,
      paddingBottom: 0,
      backgroundColor: isLight ? `${colors.DropdownV2?.menuItemBackground} !important` : '#333333 !important',
      '& .MuiMenuItem-root': {
        fontWeight: '500 !important',
        fontFamily: 'Montserrat',
        fontSize: '14px !important',
        padding: '8px 16px !important',
        backgroundColor: isLight ? `${colors.DropdownV2?.menuItemBackground} !important` : '#333333 !important',
        color: isLight ? `${colors.DropdownV2?.menuItemText} !important` : '#ffffff !important',
        '&:hover': {
          backgroundColor: isLight ? `${colors.DropdownV2?.menuItemHover} !important` : '#39ACE7 !important',
          color: isLight ? `${colors.DropdownV2?.menuItemHoverText} !important` : '#ffffff !important',
        },
        '&.Mui-selected': {
          backgroundColor: isLight ? `${colors.DropdownV2?.menuItemSelectedBg} !important` : '#39ACE7 !important',
          color: isLight ? `${colors.DropdownV2?.menuItemSelectedText} !important` : '#ffffff !important',
          fontWeight: '400 !important',
        },
        '&.Mui-selected:hover': {
          backgroundColor: isLight ? `${colors.DropdownV2?.menuItemSelectedBg} !important` : '#39ACE7 !important',
          color: isLight ? `${colors.DropdownV2?.menuItemSelectedText} !important` : '#ffffff !important',
        },
      },
    },
  },
});

const WidgetDropdown = ({
  dropdownValue,
  dropdownOptions,
  onDropdownChange,
  isLight,
  colors
}: any) => (
  <CustomDropdown
    value={dropdownValue}
    options={dropdownOptions}
    handleChange={(e) => onDropdownChange?.(e.target.value)}
    boldSelectedValue={true}
    dropdownIcon={KeyboardArrowDownIcon}
    boxProps={{ sx: getDropdownSx(isLight, colors) }}
    selectComponentProps={{
      sx: {
        color: isLight ? colors.DropdownV2?.menuItemText : '#ffffff',
        fontWeight: 600,
        fontFamily: 'Montserrat',
        '& .MuiSelect-select': {
          fontWeight: 600,
          fontFamily: 'Montserrat',
        }
      }
    }}
    menuProps={{
      ...getMenuSx(isLight, colors),
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left'
      },
      disablePortal: true,
    }}
    className="total-cost-spends-dropdown"
  />
);

const ChartInfoTooltip = ({ tooltipText }: any) => (
  <Box sx={{ position: 'absolute', top: 12, right: 8, zIndex: 10 }}>
    <InfoTooltip label={tooltipText} />
  </Box>
);

// Main Component
const ProgressChartWidget = ({
  isLoading = false,
  isOverlayWidget = false,
  title = '',
  tooltipLabel = null,
  data,
  envNames,
  seriesData,
  minTitleHeight = '50.5px',
  handleEvent,
  chartByCost,
  axisMax,
  containerProps,
  hasTooltip = false,
  tooltipText,
  hasDropdown = false,
  dropdownValue = '',
  dropdownOptions = [],
  onDropdownChange,
  variant = 'dark',
  maxVisibleBars,
  chartMinHeight = '430px',
  chartTop = '0px'
}: Readonly<ProgressChartWidgetProps>) => {
  const theme = useTheme();
  const colors = theme.palette;
  const isLight = variant === 'light';
  const dataLength = data?.length ?? 0;
  const useScroll = maxVisibleBars != null && dataLength > maxVisibleBars;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [gridLeft, setGridLeft] = useState('5%');

  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartContainerRef.current) {
        const containerWidth = chartContainerRef.current.offsetWidth;
        const isCostChart = chartByCost === 0 || chartByCost === 1;
        const calculatedLeft = calculateGridLeft(containerWidth, isCostChart, title);
        setGridLeft(calculatedLeft);
        setWindowSize(window.innerWidth);
      }
    }, 200);

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chartByCost, title]);

  const result = getProgressChartOptions(
    data ?? [],
    envNames,
    seriesData,
    chartContainerRef?.current?.offsetWidth,
    chartByCost,
    axisMax
  );

  let adjustedOption = getBaseChartOption(result, gridLeft, seriesData, defaultOptions);
  
  if (isLight) {
    adjustedOption = getLightThemeOption(adjustedOption, colors);
  }

  if (shouldShowScroll(useScroll, dataLength, chartByCost) && maxVisibleBars) {
    adjustedOption = getScrollOption(adjustedOption, maxVisibleBars, dataLength, isLight, colors);
  }

  const handleChartClick = (params: ECElementEvent) => {
    if (handleEvent && data?.length) {
      const fullProgramName = data[data.length - 1 - params.dataIndex]?.programname;
      handleEvent(params, fullProgramName);
    }
  };

  const chartEvents = handleEvent ? { click: handleChartClick } : undefined;

  return (
    <Box display="flex" flexGrow={1} height="100%" position="relative">
      <Stack
        minHeight="100px"
        p={2}
        bgcolor={isLight ? colors.widget?.background?.chart : 'primary.main'}
        fontFamily="Montserrat"
        borderRadius="5px"
        flexGrow={1}
        {...containerProps}
      >
        <Stack
          direction="row"
          gap={2}
          justifyContent="space-between"
          alignItems="start"
          minHeight={minTitleHeight}
        >
          <WidgetTitle
            title={title}
            isOverlayWidget={isOverlayWidget}
            isLight={isLight}
            tooltipLabel={tooltipLabel}
            colors={colors}
          />

          {hasDropdown && dropdownOptions.length > 0 && (
            <WidgetDropdown
              dropdownValue={dropdownValue}
              dropdownOptions={dropdownOptions}
              onDropdownChange={onDropdownChange}
              isLight={isLight}
              colors={colors}
            />
          )}
        </Stack>

        <Stack
          minHeight={chartMinHeight}
          top={chartTop}
          position="relative"
          flexGrow={1}
          key={windowSize}
          justifyContent="center"
          ref={chartContainerRef}
        >
          {!isLoading ? (
            <ReactECharts
              style={{ width: '100%', height: '100%' }}
              option={adjustedOption}
              onEvents={chartEvents}
              opts={{ renderer: 'canvas', width: 'auto' }}
              notMerge={true}
              lazyUpdate={true}
            />
          ) : (
            <LoaderComponent />
          )}
        </Stack>
      </Stack>

      {hasTooltip && tooltipText && (
        <ChartInfoTooltip tooltipText={tooltipText} />
      )}
    </Box>
  );
};

export default ProgressChartWidget;